import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Upload, Image, X, Save, Eye, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import RichTextEditor from '@/components/RichTextEditor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { createArticle, updateArticle, getAdminArticles, getAdminWriters, verifyAdmin, uploadToCloudinary, generateArticleImage } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [writers, setWriters] = useState([]);
  const [role, setRole] = useState('admin');
  const [writerName, setWriterName] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'news',
    image_url: '',
    tags: [],
    featured: false,
    trending: false,
    author_id: '',
    author_name: ''
  });
  const [tagInput, setTagInput] = useState('');

  const fetchArticle = useCallback(async () => {
    if (!id) {
      setInitialLoading(false);
      return;
    }

    try {
      const articles = await getAdminArticles();
      const article = articles.find(a => a.id === id);
      if (article) {
        setFormData({
          title: article.title || '',
          excerpt: article.excerpt || '',
          content: article.content || '',
          category: article.category || 'news',
          image_url: article.image_url || '',
          tags: article.tags || [],
          featured: article.featured || false,
          trending: article.trending || false,
          author_id: article.author_id || '',
          author_name: article.author_name || ''
        });
      } else {
        toast.error('Article not found');
        navigate('/admin/dashboard');
      }
    } catch (error) {
      toast.error('Failed to load article');
      navigate('/admin/dashboard');
    } finally {
      setInitialLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    const checkAuth = async () => {
      const user = await verifyAdmin();
      if (!user) {
        navigate('/admin');
        return;
      }
      const userRole = user.role || 'admin';
      setRole(userRole);
      setWriterName(user.writer_name || '');
      // Fetch writers list only for admins (writers are auto-assigned)
      if (userRole === 'admin') {
        try {
          const writersList = await getAdminWriters();
          setWriters(writersList);
        } catch (e) {
          console.error('Failed to load writers', e);
        }
      }
      fetchArticle();
    };
    checkAuth();
  }, [navigate, fetchArticle]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const result = await uploadToCloudinary(file);
      setFormData({ ...formData, image_url: result.secure_url });
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image. Please check Cloudinary configuration.');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!formData.title.trim()) {
      toast.error('Enter a title first so the AI knows what to create');
      return;
    }
    setGenerating(true);
    toast.info('Generating AI image... this can take 15-30 seconds');
    try {
      const result = await generateArticleImage(formData.title, formData.content || formData.excerpt || '');
      setFormData(prev => ({ ...prev, image_url: result.url }));
      toast.success('AI image generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate image');
    } finally {
      setGenerating(false);
    }
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.excerpt.trim()) {
      toast.error('Excerpt is required');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Content is required');
      return;
    }

    setLoading(true);
    try {
      if (isEditing) {
        await updateArticle(id, formData);
        toast.success('Article updated');
      } else {
        await createArticle(formData);
        toast.success('Article created');
      }
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save article');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="admin-editor">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-surface border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link 
              to="/admin/dashboard"
              className="flex items-center gap-2 text-text-secondary hover:text-white transition-colors"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-body">Back</span>
            </Link>
            <div className="flex items-center gap-4">
              {formData.image_url && (
                <a 
                  href={`/article/${formData.title?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-text-secondary hover:text-white"
                >
                  <Eye className="w-5 h-5" />
                </a>
              )}
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-[#CCFF00] text-black hover:bg-[#b3e600] font-bold uppercase tracking-wide"
                data-testid="save-article"
              >
                {loading ? (
                  <span className="spinner border-black border-t-transparent w-4 h-4" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {isEditing ? 'Update' : 'Publish'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Title */}
          <div>
            <Label htmlFor="title" className="text-white text-lg">Title</Label>
            <Input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter article title..."
              className="mt-2 h-14 text-2xl font-heading font-bold bg-transparent border-zinc-800 text-white focus:border-[#CCFF00] uppercase"
              data-testid="article-title-input"
            />
          </div>

          {/* Category & Status Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label className="text-white">Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="mt-2 bg-surface border-zinc-800 text-white" data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface border-zinc-800">
                  <SelectItem value="news" className="text-white hover:bg-[#CCFF00]/10">News</SelectItem>
                  <SelectItem value="memes" className="text-white hover:bg-[#CCFF00]/10">Memes</SelectItem>
                  <SelectItem value="race-updates" className="text-white hover:bg-[#CCFF00]/10">Race Updates</SelectItem>
                  <SelectItem value="reels" className="text-white hover:bg-[#CCFF00]/10">Reels</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-4 bg-surface border border-zinc-800">
              <Label className="text-white">Featured</Label>
              <Switch
                checked={formData.featured}
                onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                data-testid="featured-switch"
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-surface border border-zinc-800">
              <Label className="text-white">Trending</Label>
              <Switch
                checked={formData.trending}
                onCheckedChange={(checked) => setFormData({ ...formData, trending: checked })}
                data-testid="trending-switch"
              />
            </div>
          </div>

          {/* Writer Selection */}
          <div>
            <Label className="text-white">Writer</Label>
            {role === 'writer' ? (
              <div className="mt-2 px-4 py-3 bg-surface border border-zinc-800 text-white font-body" data-testid="writer-auto-assigned">
                {writerName || 'You'}
                <span className="ml-2 font-mono text-xs text-text-secondary">(auto-assigned)</span>
              </div>
            ) : (
              <>
                <Select
                  value={formData.author_id || "none"}
                  onValueChange={(value) => {
                    if (value === "none") {
                      setFormData({ ...formData, author_id: '', author_name: '' });
                    } else {
                      const selected = writers.find(w => w.id === value);
                      setFormData({ ...formData, author_id: value, author_name: selected?.name || '' });
                    }
                  }}
                >
                  <SelectTrigger className="mt-2 bg-surface border-zinc-800 text-white" data-testid="writer-select">
                    <SelectValue placeholder="Select a writer..." />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-zinc-800">
                    <SelectItem value="none" className="text-white/50 hover:bg-[#CCFF00]/10">No writer</SelectItem>
                    {writers.map((w) => (
                      <SelectItem key={w.id} value={w.id} className="text-white hover:bg-[#CCFF00]/10">{w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {writers.length === 0 && (
                  <p className="mt-2 text-xs text-text-secondary">
                    No writers yet.{' '}
                    <Link to="/admin/writer/new" className="text-[#CCFF00] hover:underline">Add one</Link>
                  </p>
                )}
              </>
            )}
          </div>

          {/* Image Upload */}
          <div>
            <Label className="text-white">Cover Image</Label>
            <div className="mt-2">
              {formData.image_url ? (
                <div className="relative aspect-video bg-surface border border-zinc-800 overflow-hidden">
                  <img 
                    src={formData.image_url}
                    alt="Cover"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, image_url: '' })}
                    className="absolute top-4 right-4 p-2 bg-black/80 text-white hover:bg-red-500 transition-colors"
                    data-testid="remove-image"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center aspect-video bg-surface border-2 border-dashed border-zinc-800 hover:border-[#CCFF00] cursor-pointer transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="image-upload-input"
                  />
                  {uploading ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      <Image className="w-12 h-12 text-text-secondary mb-4" />
                      <p className="font-body text-text-secondary">Click to upload cover image</p>
                      <p className="font-mono text-xs text-text-secondary mt-2">Max 10MB • JPG, PNG, WebP</p>
                    </>
                  )}
                </label>
              )}
            </div>
            {/* Manual URL input as fallback */}
            <div className="mt-4">
              <Input
                type="url"
                placeholder="Or paste image URL..."
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="bg-surface border-zinc-800 text-white focus:border-[#CCFF00]"
                data-testid="image-url-input"
              />
            </div>
            {/* AI Image Generation */}
            <Button
              type="button"
              variant="outline"
              onClick={handleGenerateImage}
              disabled={generating || !formData.title.trim()}
              className="w-full mt-4 border-[#CCFF00]/40 text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black hover:border-[#CCFF00] font-bold uppercase tracking-wide"
              data-testid="generate-ai-image-button"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating AI Image...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Image with AI
                </>
              )}
            </Button>
            {!formData.title.trim() && (
              <p className="mt-2 font-mono text-xs text-text-secondary">Enter a title to enable AI image generation</p>
            )}
          </div>

          {/* Excerpt */}
          <div>
            <Label htmlFor="excerpt" className="text-white">Excerpt</Label>
            <Textarea
              id="excerpt"
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              placeholder="Brief summary for cards and previews..."
              rows={3}
              className="mt-2 bg-surface border-zinc-800 text-white focus:border-[#CCFF00] resize-none"
              data-testid="article-excerpt-input"
            />
          </div>

          {/* Content */}
          <div>
            <Label htmlFor="content" className="text-white">Content</Label>
            <div className="mt-2">
              <RichTextEditor
                content={formData.content}
                onChange={(html) => setFormData({ ...formData, content: html })}
                placeholder="Write your article content here..."
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <Label className="text-white">Tags</Label>
            <div className="mt-2 flex flex-wrap gap-2 mb-3">
              {formData.tags.map((tag) => (
                <span 
                  key={tag}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-[#CCFF00]/10 border border-[#CCFF00]/30 text-[#CCFF00] text-sm"
                >
                  #{tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="hover:text-red-400"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddTag(e)}
                placeholder="Add a tag..."
                className="bg-surface border-zinc-800 text-white focus:border-[#CCFF00]"
                data-testid="tag-input"
              />
              <Button
                type="button"
                onClick={handleAddTag}
                variant="outline"
                className="border-zinc-800 text-white hover:bg-[#CCFF00] hover:text-black hover:border-[#CCFF00]"
                data-testid="add-tag-button"
              >
                Add
              </Button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
